/**
 * autoBackupService.ts
 * ─────────────────────
 * Auto Local Backup — Kaam 3
 *
 * Features:
 *  - Configurable interval: 30min / 1hr / 3hr / daily
 *  - JSON export via @tauri-apps/plugin-fs to local folder
 *  - Last 5 backups retain, purane auto-delete
 *  - Web fallback: browser download if not in Tauri
 *  - Settings stored in localStorage
 */

import { db } from '../db/db';

// ── Types ────────────────────────────────────────────────────────────────────
export type BackupInterval = '30min' | '1hr' | '3hr' | 'daily' | 'off';

export interface AutoBackupSettings {
  interval: BackupInterval;
  folder: string;       // user-chosen folder path (Tauri only)
  maxBackups: number;   // how many to retain (default 5)
  lastBackupAt: string; // ISO timestamp
}

export interface BackupMeta {
  filename: string;
  createdAt: string;
  companyId: number;
  companyName: string;
  sizeBytes: number;
}

// ── Constants ────────────────────────────────────────────────────────────────
const SETTINGS_KEY = 'aarthika_auto_backup_settings';
const META_KEY     = 'aarthika_auto_backup_meta';   // list of last N backups
const DEFAULT_MAX_BACKUPS = 5;

const INTERVAL_MS: Record<BackupInterval, number> = {
  '30min': 30 * 60 * 1000,
  '1hr':   60 * 60 * 1000,
  '3hr':   3  * 60 * 60 * 1000,
  'daily': 24 * 60 * 60 * 1000,
  'off':   Infinity,
};

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Detect Tauri environment safely */
const isTauri = (): boolean => {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
};

export const getAutoBackupSettings = (): AutoBackupSettings => {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return {
    interval: 'off',
    folder: '',
    maxBackups: DEFAULT_MAX_BACKUPS,
    lastBackupAt: '',
  };
};

export const saveAutoBackupSettings = (settings: AutoBackupSettings): void => {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
};

const getBackupMeta = (): BackupMeta[] => {
  try {
    const raw = localStorage.getItem(META_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return [];
};

const saveBackupMeta = (meta: BackupMeta[]): void => {
  localStorage.setItem(META_KEY, JSON.stringify(meta));
};

// ── Core: Build backup payload ───────────────────────────────────────────────
const buildBackupPayload = async (companyId: number): Promise<{ payload: object; companyName: string }> => {
  const company = await db.companies.get(companyId);
  if (!company) throw new Error(`Company ${companyId} not found`);

  const [groups, ledgers, vouchers, stockGroups, stockItems,
         stockCategories, units, locations, currencies, voucherTypes, employees] = await Promise.all([
    db.groups.where('companyId').equals(companyId).toArray(),
    db.ledgers.where('companyId').equals(companyId).toArray(),
    db.vouchers.where('companyId').equals(companyId).toArray(),
    db.stockGroups.where('companyId').equals(companyId).toArray(),
    db.stockItems.where('companyId').equals(companyId).toArray(),
    db.stockCategories.where('companyId').equals(companyId).toArray(),
    db.units.where('companyId').equals(companyId).toArray(),
    db.locations.where('companyId').equals(companyId).toArray(),
    db.currencies.where('companyId').equals(companyId).toArray(),
    db.voucherTypes.where('companyId').equals(companyId).toArray(),
    (db as any).employees ? (db as any).employees.where('companyId').equals(companyId).toArray() : Promise.resolve([]),
  ]);

  const vIds = vouchers.map(v => v.id!).filter(Boolean);
  const [voucherEntries, inventoryEntries] = await Promise.all([
    vIds.length > 0 ? db.voucherEntries.where('voucherId').anyOf(vIds).toArray() : Promise.resolve([]),
    vIds.length > 0 ? db.inventoryEntries.where('voucherId').anyOf(vIds).toArray() : Promise.resolve([]),
  ]);

  return {
    payload: {
      version: '1.1',
      timestamp: new Date().toISOString(),
      company,
      groups, ledgers, vouchers, voucherEntries, inventoryEntries,
      stockGroups, stockItems, stockCategories, units, locations,
      currencies, voucherTypes, employees,
    },
    companyName: company.name,
  };
};

// ── Write via Tauri fs ───────────────────────────────────────────────────────
const writeTauriBackup = async (
  json: string,
  folder: string,
  filename: string
): Promise<void> => {
  const { writeTextFile, mkdir } = await import('@tauri-apps/plugin-fs');
  // Ensure folder exists (best-effort)
  try { await mkdir(folder, { recursive: true }); } catch {}
  const fullPath = `${folder}/${filename}`;
  await writeTextFile(fullPath, json);
};

// ── Prune old backups (keep last N) ─────────────────────────────────────────
const pruneOldBackups = async (
  companyId: number,
  folder: string,
  maxBackups: number
): Promise<void> => {
  const allMeta = getBackupMeta();
  const companyMeta = allMeta
    .filter(m => m.companyId === companyId)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt)); // oldest first

  if (companyMeta.length <= maxBackups) return;

  const toDelete = companyMeta.slice(0, companyMeta.length - maxBackups);

  if (isTauri() && folder) {
    try {
      const { remove } = await import('@tauri-apps/plugin-fs');
      for (const old of toDelete) {
        try {
          await remove(`${folder}/${old.filename}`);
        } catch {
          // File may already be gone — ignore
        }
      }
    } catch {}
  }

  // Update meta list
  const deletedFilenames = new Set(toDelete.map(m => m.filename));
  const updatedMeta = allMeta.filter(m => !(m.companyId === companyId && deletedFilenames.has(m.filename)));
  saveBackupMeta(updatedMeta);
};

// ── Web fallback: browser download ───────────────────────────────────────────
const browserDownloadBackup = (json: string, filename: string): void => {
  const blob = new Blob([json], { type: 'application/json' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ── Main: perform one backup ─────────────────────────────────────────────────
export const performBackup = async (
  companyId: number,
  settings?: AutoBackupSettings,
  silent = false           // true = auto-backup (no browser download fallback dialog)
): Promise<{ success: boolean; filename: string; message: string }> => {
  const cfg = settings ?? getAutoBackupSettings();

  try {
    const { payload, companyName } = await buildBackupPayload(companyId);
    const safeName = companyName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
    const dateStr  = new Date().toISOString().replace(/[:.]/g, '-').split('T').join('_T');
    const filename = `${safeName}_AutoBackup_${dateStr}.json`;
    const json     = JSON.stringify(payload, null, 2);

    if (isTauri() && cfg.folder) {
      await writeTauriBackup(json, cfg.folder, filename);
      await pruneOldBackups(companyId, cfg.folder, cfg.maxBackups);
    } else if (!silent) {
      // Web / no folder → trigger browser download
      browserDownloadBackup(json, filename);
    } else {
      // Silent auto-backup without Tauri folder — skip (nothing to write to)
      return { success: false, filename, message: 'No backup folder configured. Set a folder in Backup Settings.' };
    }

    // Record meta
    const meta = getBackupMeta();
    meta.push({
      filename,
      createdAt: new Date().toISOString(),
      companyId,
      companyName,
      sizeBytes: json.length,
    });
    saveBackupMeta(meta);

    // Update lastBackupAt
    const updated = { ...cfg, lastBackupAt: new Date().toISOString() };
    saveAutoBackupSettings(updated);

    return { success: true, filename, message: `Backup saved: ${filename}` };
  } catch (err: any) {
    return { success: false, filename: '', message: `Backup failed: ${err?.message || err}` };
  }
};

// ── Auto-backup timer ────────────────────────────────────────────────────────
let _autoBackupTimer: ReturnType<typeof setTimeout> | null = null;

const scheduleNext = (companyId: number, settings: AutoBackupSettings): void => {
  if (_autoBackupTimer) clearTimeout(_autoBackupTimer);
  if (settings.interval === 'off') return;

  const intervalMs = INTERVAL_MS[settings.interval];
  const lastAt     = settings.lastBackupAt ? new Date(settings.lastBackupAt).getTime() : 0;
  const now        = Date.now();
  const elapsed    = now - lastAt;
  const delay      = Math.max(0, intervalMs - elapsed);

  _autoBackupTimer = setTimeout(async () => {
    const fresh = getAutoBackupSettings();
    if (fresh.interval === 'off') return;
    await performBackup(companyId, fresh, true /* silent */);
    scheduleNext(companyId, getAutoBackupSettings());
  }, delay);
};

/**
 * Call this when app starts or company changes.
 * Starts (or restarts) the auto-backup timer for the given company.
 */
export const startAutoBackup = (companyId: number): void => {
  const settings = getAutoBackupSettings();
  if (settings.interval === 'off') return;
  scheduleNext(companyId, settings);
};

/**
 * Call when company is deselected / app exits.
 */
export const stopAutoBackup = (): void => {
  if (_autoBackupTimer) {
    clearTimeout(_autoBackupTimer);
    _autoBackupTimer = null;
  }
};

/**
 * Restart timer — call after saving new settings.
 */
export const restartAutoBackup = (companyId: number): void => {
  stopAutoBackup();
  startAutoBackup(companyId);
};

export const getRecentBackups = (companyId?: number): BackupMeta[] => {
  const all = getBackupMeta();
  const filtered = companyId !== undefined ? all.filter(m => m.companyId === companyId) : all;
  return filtered.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
};
